<script lang="ts">
    import { blur } from "svelte/transition"
    import { getCachedEntries } from "../../../api"
    import { location } from "../../store"
    import { applyFilterFromHash, filter2Hash } from "../../utils"
    import { spaLink } from "../../actions"

    export let block: ContentBlockEntry

    let loading = true

    let retailerEnties: RetailerEntry[]
    let countryEntries: CountryEntry[]
    let countryMap: { [key: string]: CountryEntry } = {}
    let regionEntries: RegionEntry[]
    let countriesByRegion: Map<string, CountryEntry[]> = new Map()

    async function load() {
        loading = true
        let retailerFilter = null
        if (block.retailers?.fixedType) {
            retailerFilter = { types: block.retailers.fixedType }
        }

        retailerEnties = await getCachedEntries("retailer", retailerFilter, "name")

        let countriesWithRetailers: string[] = []
        retailerEnties.forEach((retailer) => {
            if (retailer.countryCodes) {
                retailer.countryCodes.forEach((countryCode) => {
                    if (!countriesWithRetailers.includes(countryCode)) {
                        countriesWithRetailers.push(countryCode)
                    }
                })
            }
        })

        countryEntries = await getCachedEntries("country", { code: { $in: countriesWithRetailers } }, "name")
        let regionsWithCountries: string[] = []
        countryEntries.forEach((country) => {
            if (!regionsWithCountries.includes(country.region)) {
                regionsWithCountries.push(country.region)
            }
        })

        regionEntries = await getCachedEntries("region", { _id: { $in: regionsWithCountries } }, "menuOrder")
        countryEntries.forEach((entry) => {
            countryMap[entry.code] = entry
        })
        // assign countries to regions
        regionEntries.forEach((region) => {
            countriesByRegion.set(
                region.id,
                countryEntries.filter((country) => country.region === region.id)
            )
        })

        retailerEnties = retailerEnties
            .sort((a, b) => {
                // sort by country last
                const countryA = countryMap[a.countryCodes?.[0]]
                const countryB = countryMap[b.countryCodes?.[0]]
                return countryA?.name.localeCompare(countryB?.name) || 0
            })
            .sort((a, b) =>
                // sort then salesPoints second
                a.types.includes("salesPoint") && !b.types.includes("salesPoint") ? -1 : 1
            )
            .sort((a, b) =>
                // sort retailPartners first (must be sorted last to get correct order)
                a.types.includes("retailPartner") && !b.types.includes("retailPartner") ? -1 : 1
            )

        loading = false
    }

    load()

    let retailerFilter: { [key: string]: string[] } = {
        country: [],
        type: [],
    }

    let filterInitialized = false

    // first apply filter from initial hash
    $: if (filterInitialized) {
        filter2Hash(retailerFilter, true)
    }

    let filteredRetailers: RetailerEntry[]
    function applyFilter(hash: string) {
        const f = applyFilterFromHash(hash, { country: "countryCodes", type: "types" }, retailerEnties, () => {
            if (typeof window !== "undefined") {
                setTimeout(() => {
                    scrollTargetContainer?.scrollIntoView({ behavior: "instant", block: "start", inline: "nearest" })
                    setTimeout(() => {
                        scrollTargetContainer?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" })
                    }, 600) // after in-view scroll animation
                }, 10)
            }
        })
        filteredRetailers = f.filteredItems
        if (f.newFilter) {
            retailerFilter = f.newFilter
        }
        filterInitialized = true
    }

    $: if (!loading) {
        applyFilter($location.hash)
    }

    let outerWidth = 0

    let scrollTargetContainer: HTMLDivElement

    let openRegionFilter: { [key: string]: boolean } = {}
    // open Europe by default // dont open europe by default (18.06.24)
    /* $: if (regionEntries && outerWidth > 996) {
        regionEntries.forEach((region) => {
            openRegionFilter[region.id] = region.name === "Europe"
        })
    } */

    let typeFilterOpen = false

    function getTypeLabel(type: string) {
        return (
            { retailPartner: "Retail partner", salesPoint: "Sales point", siacBatteryService: "SIAC battery service" }[
                type
            ] ||
            type ||
            ""
        )
    }
</script>

<svelte:window bind:outerWidth />

<!-- position 100px above -->
<div style="position:relative;">
    <div bind:this="{scrollTargetContainer}" style="position: absolute; top: -100px"></div>
</div>

<div class="container" data-block-cfg="{block.retailers}">
    <div class="grid align-start">
        <div class="col-md-3 gray-gradient col-grid gap-s filter-col">
            {#if !block.retailers?.fixedType}
                <div class="col-auto filters-wrapper">
                    <button
                        class="h4 filter-headline vp-m"
                        aria-expanded="{typeFilterOpen}"
                        on:click="{() => (typeFilterOpen = !typeFilterOpen)}"
                        >Type
                    </button>
                    <div class="option-group gap-s" class:open="{typeFilterOpen}">
                        <input
                            type="checkbox"
                            id="type_check_retail_partner"
                            bind:group="{retailerFilter.type}"
                            value="retailPartner"
                        />
                        <label for="type_check_retail_partner">
                            <div class="checkbox-pill">{getTypeLabel("retailPartner")}</div></label
                        >
                        <input
                            type="checkbox"
                            id="type_check_sales_point"
                            bind:group="{retailerFilter.type}"
                            value="salesPoint"
                        />
                        <label for="type_check_sales_point"
                            ><div class="checkbox-pill">{getTypeLabel("salesPoint")}</div></label
                        >
                        <input
                            type="checkbox"
                            id="type_check_siac_battery_service"
                            bind:group="{retailerFilter.type}"
                            value="siacBatteryService"
                        />
                        <label for="type_check_siac_battery_service"
                            ><div class="checkbox-pill">{getTypeLabel("siacBatteryService")}</div></label
                        >
                    </div>
                </div>
            {/if}
            {#each regionEntries || [] as region}
                <div class="col-auto filters-wrapper">
                    <button
                        class="h4 filter-headline with-toggle"
                        data-region="{region.name}"
                        aria-expanded="{openRegionFilter[region.id]}"
                        on:click="{() => (openRegionFilter[region.id] = !openRegionFilter[region.id])}"
                        class:bp-m="{openRegionFilter[region.id]}"
                        >{region.name}
                    </button>
                    <div class="option-group gap-s toggle" class:open="{openRegionFilter[region.id]}">
                        {#each countriesByRegion.get(region.id) || [] as country}
                            <input
                                type="checkbox"
                                id="country_check_{country.id}"
                                bind:group="{retailerFilter.country}"
                                value="{country.code}"
                            />
                            <label for="country_check_{country.id}">{country.name}</label>
                        {/each}
                    </div>
                </div>
            {/each}
        </div>
        <div class="col-md-9">
            <section class="retailers">
                <div class="retailers-grid vp-xl hp-xl gap-m">
                    {#each filteredRetailers || [] as retailer}
                        <div class="retailer-item white-bg border-radius-all border-radius-m vp-m hp-m">
                            <div class="retailer-card" transition:blur="{{ amount: 10 }}">
                                {#if retailer.types?.length}
                                    <div class="retailer-types bp-m">
                                        {#each retailer.types as type}
                                            <span>{getTypeLabel(type)}</span>
                                        {/each}
                                    </div>
                                {/if}
                                <b>{retailer.name}</b><br />
                                {#if retailer.url}
                                    <a href="{retailer.url}" target="_blank" class="btn-external">{retailer.url}</a>
                                {/if}
                                {#if retailer.email}
                                    <a href="mailto:{retailer.email}" class="btn-email">{retailer.email}</a>
                                {/if}
                                {#if retailer.phone}
                                    <a href="tel:{retailer.phone}" class="btn-phone">{retailer.phone}</a>
                                {/if}
                                {#if retailer.mobile}
                                    <a href="tel:{retailer.mobile}" class="btn-phone">{retailer.mobile}</a>
                                {/if}
                                {#if retailer.siacBatteryService?.url}
                                    <a
                                        use:spaLink
                                        class="btn-batteryService tp-m"
                                        href="{retailer.siacBatteryService.url}"
                                        target="{retailer.siacBatteryService.url.match(/^https?:\/\//)
                                            ? '_blank'
                                            : undefined}">SIAC battery service</a
                                    >
                                {/if}
                                {#if retailer.countryCodes}
                                    <div class="tp-m country-list">
                                        {#each retailer.countryCodes as countryCode}
                                            <span>{countryMap[countryCode].name}</span>
                                        {/each}
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/each}
                </div>
            </section>
        </div>
    </div>
</div>
